import React, { useEffect, useState } from "react"
import SbEditable from "storyblok-react"
import StoryblokClient from "storyblok-js-client"
import Components from "../components"
import MetaData from "../components/s22_meta-data"
import OverlayCmp from "../components/s22_overlay"
import { pageBlokType, globalDataType, storyType } from "../types"
import { useAppContext, generatePayload } from "../helpers/appContext"

import { InteractionStatus } from "@azure/msal-browser"
import { useMsal, useIsAuthenticated } from "@azure/msal-react"

import Cookies from "js-cookie"

interface PageLayoutProps {
  globalData: globalDataType
  blok: pageBlokType
  path: string
  story: storyType
}

const PageLayout: React.FC<PageLayoutProps> = ({
  globalData,
  blok,
  path,
  story,
}) => {
  let tags = (story && story.tag_list) || []
  const { state, dispatch } = useAppContext()

  const getDispatch = (value: string) => {
    switch (value) {
      case "degree":
        return dispatch(generatePayload("Degree", story))
      case "program":
        return dispatch(generatePayload("Program", story))
      case "module":
        return dispatch(generatePayload("Module", story))
      case "location":
        return dispatch(generatePayload("Campus", story))
      default:
        return
    }
  }

  useEffect(() => {
    const handleSearch = async () => {
      const Storyblok = new StoryblokClient({
        accessToken: process.env.GATSBY_SB_TOKEN,
      })

      if (tags && story) {
        dispatch(generatePayload("Tags", story))

        const degreeRes = await Storyblok.get(`cdn/stories/`, {
          with_tag: ["degree"],
          cv: Date.now(),
        })
        const programRes = await Storyblok.get(`cdn/stories/`, {
          with_tag: ["program"],
          cv: Date.now(),
        })

        // Find the dynamically generated program tag
        const dynamicDegreeTag = tags.find((tag: string) =>
          tag.toLowerCase().startsWith("degree_")
        )
        const dynamicProgramTag = tags.find((tag: string) =>
          tag.toLowerCase().startsWith("program_")
        )

        if (dynamicDegreeTag) {
          // Filter stories with the dynamic program tag
          const matchingDegreeStories = degreeRes.data.stories.filter(
            (story: storyType) => story.tag_list?.includes(dynamicDegreeTag)
          )
          if (matchingDegreeStories.length > 0) {
            dispatch(generatePayload("Degree", matchingDegreeStories[0]))
          }
        }
        if (dynamicProgramTag) {
          // Filter stories with the dynamic program tag
          const matchingProgramStories = programRes.data.stories.filter(
            (story: storyType) => story.tag_list?.includes(dynamicProgramTag)
          )
          if (matchingProgramStories.length > 0) {
            dispatch(generatePayload("Program", matchingProgramStories[0]))
          }
        }
      }
    }
    handleSearch()
  }, [])

  useEffect(() => {
    if (tags && tags.length > 0 && story) {
      if (tags.find((tag: string) => tag.toLowerCase() === "program")) {
        updatePageInfo("Program", "id", "name", "url", "imageUrl")
      }
      if (tags.find((tag: string) => tag.toLowerCase() === "degree")) {
        updatePageInfo("Degree", "id", "name", "url", "imageUrl")
      }
      if (tags.find((tag: string) => tag.includes("degree_"))) {
        if (tags.find((tag: string) => tag.includes("program_"))) {
          updatePageInfo("Program", "id", "name", "url", "imageUrl")
        } else {
          updatePageInfo("Degree", "id", "name", "url", "imageUrl")
        }
      }
      if (tags.find((tag: string) => tag.toLowerCase() === "module")) {
        updatePageInfo("Module", "id", "name", "url", "imageUrl")
      }
      if (tags.find((tag: string) => tag.toLowerCase() === "location")) {
        updatePageInfo("Campus", "id", "name", "url", "imageUrl")
      }
    }
  }, [state.catalogInfo])

  useEffect(() => {
    dispatch({
      type: "UPDATE_PAGE_INFO",
      payload: {
        name:
          story &&
          (story.content as pageBlokType).meta_data[0] &&
          (story.content as pageBlokType).meta_data[0].meta_title &&
          (story.content as pageBlokType).meta_data[0].meta_title.replace(
            /\s\|\sHult\sInternational\sBusiness\sSchool$/,
            ""
          ),
        tags: tags,
      },
    })

    if (document) {
      const dataForms = document.querySelectorAll("[data-name=form]")
      const dataVideos = document.querySelectorAll("[data-name=video]")

      if (dataForms.length > 0) {
        dispatch({
          type: "UPDATE_PAGE_INFO",
          payload: {
            hasForm: true,
          },
        })
      }
      if (dataVideos.length > 0) {
        dispatch({
          type: "UPDATE_PAGE_INFO",
          payload: {
            hasVideo: true,
          },
        })
      }
    }
  }, [story, tags])

  const updatePageInfo = (
    type: string,
    idField: string,
    nameField: string,
    urlField?: string,
    imageUrlField?: string
  ) => {
    if (state.catalogInfo[type].id) {
      const relatedCatalogObjects: { [key: string]: string[] } = {}
      Object.keys(state.catalogInfo).forEach((catalogType) => {
        if (catalogType !== type && state.catalogInfo[catalogType].id) {
          if (!relatedCatalogObjects[catalogType]) {
            relatedCatalogObjects[catalogType] = []
          }
          relatedCatalogObjects[catalogType].push(
            state.catalogInfo[catalogType].id
          )
        }
      })

      // Include tags in relatedCatalogObjects
      if (tags && tags.length > 0) {
        relatedCatalogObjects.Tags = tags
      }

      dispatch({
        type: "UPDATE_PAGE_INFO",
        payload: {
          tags: [...tags],
          catalog: {
            [type]: {
              id: state.catalogInfo[type].id,
              name: state.catalogInfo[type][nameField],
              ...(urlField && { url: state.catalogInfo[type][urlField] }),
              ...(imageUrlField && {
                imageUrl: state.catalogInfo[type][imageUrlField],
              }),
              relatedCatalogObjects,
            },
          },
        },
      })
    }
  }

  useEffect(() => {
    if (tags) {
      dispatch({
        type: "UPDATE_CATALOG_INFO",
        payload: {
          Tags: tags,
        },
      })
      tags.forEach((tag: string) => {
        getDispatch(tag)
      })
    }
  }, [tags])

  const { authentication_required } = blok
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  useEffect(() => {
    if (authentication_required) {
      // const userEmail = Cookies.get("hultUserEmail")
      if (inProgress === InteractionStatus.None && !isAuthenticated) {
        try {
          // if (userEmail) {
          //   instance.ssoSilent({
          //     scopes: [process.env.GATSBY_APP_AZURE_CLIENT_ID || "", "openid"],
          //     loginHint: userEmail,
          //   })
          // } else {
          instance.loginRedirect({
            scopes: [process.env.GATSBY_APP_AZURE_CLIENT_ID || "", "openid"],
          })
          // }
        } catch {
          console.log("error")
        }
      }
    }
  })

  return (
    <>
      <SbEditable content={blok}>
        <OverlayCmp />

        {blok.meta_data &&
          blok.meta_data.map((md) => {
            if (md.component === "s22_meta-data")
              return <MetaData key={md._uid} blok={md} path={path} />
            return null
          })}
        <div className="full-container">
          {blok.header &&
            blok.header.map((content) => {
              return React.createElement(Components(content.component), {
                key: content._uid,
                blok: content,
                path: content.component === "global_header" ? path : undefined,
              })
            })}
          {blok.body &&
            blok.body.map((content) => {
              return React.createElement(Components(content.component), {
                key: content._uid,
                blok: content,
              })
            })}
          {blok.popup &&
            !Array.isArray(blok.popup) &&
            blok.popup.content &&
            React.createElement(Components(blok.popup.content.component), {
              key: blok.popup.content._uid,
              blok: blok.popup.content,
            })}
          {blok.footer &&
            !Array.isArray(blok.footer) &&
            blok.footer.content &&
            React.createElement(Components(blok.footer.content.component), {
              key: blok.footer.content._uid,
              blok: blok.footer.content,
            })}
        </div>
      </SbEditable>
    </>
  )
}

export default PageLayout
